import React, { useState } from 'react';
import { Card, Button, Table, Badge, Spinner, Nav, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { NOILAYSOLIEU_API, BaseGatewayImage } from '../../../../utils/apiUrl';
import { trangThaiLaySoLieuValue, objectTrangThaiLaySoLieu } from '../../../../utils/define';
import { Get, DeleteSingle } from '../../../../utils/httpProvider';
import { toast } from 'react-toastify';
import moment from 'moment';
import NoiLaySoLieuAdd from './NoiLaySoLieuAdd';
import NoiLaySoLieuEdit from './NoiLaySoLieuEdit';
import { ConfirmModal } from '../../../../modules/modal/ConfirmModal';

const NoiLaySoLieu = ({ hoSoId, activeTab, setActiveTab }) => {
    const queryClient = useQueryClient();
    const [viewMode, setViewMode] = useState('list'); // 'list', 'add', 'edit'
    const [dataEdit, setDataEdit] = useState(null);
    
    // Delete state
    const [openConfirmDeleteModal, setOpenConfirmDeleteModal] = useState(false);
    const [idWantDelete, setIdWantDelete] = useState('');
    const [confirmDelMessage, setConfirmDelMessage] = useState('');

    const renderTooltip = (props, text) => (
        <Tooltip id="button-tooltip" {...props}>
            {text}
        </Tooltip>
    );

    const { isLoading, data: queryData, refetch } = useQuery(
        ['soLieuNghienCuuData', hoSoId],
        () => Get(`${NOILAYSOLIEU_API.GET_BY_HOSO_ID}/${hoSoId}`),
        { 
            enabled: !!hoSoId && viewMode === 'list',
            keepPreviousData: true
        }
    );

    // Xử lý data từ API
    let dataList = [];
    if (queryData) {
        if (Array.isArray(queryData)) {
            dataList = queryData;
        } else if (queryData.data && Array.isArray(queryData.data)) {
            dataList = queryData.data;
        } else if (queryData.$values) {
            dataList = queryData.$values;
        } else if (queryData.data && queryData.data.$values) {
            dataList = queryData.data.$values;
        } else {
            dataList = [queryData];
        }
    }

    const deleteFunc = useMutation((id) => DeleteSingle(NOILAYSOLIEU_API.DELETE_ITEM, id, null));

    const handleAdd = () => {
        setViewMode('add');
    };

    const handleEdit = (item) => {
        setDataEdit(item);
        setViewMode('edit');
    };

    const deleteAction = (id, name) => {
        setIdWantDelete(id);
        setConfirmDelMessage(name || 'Bản ghi này');
        setOpenConfirmDeleteModal(true);
    };

    const acceptDelete = () => {
        if (!idWantDelete) return;

        deleteFunc.mutate(idWantDelete, {
            onSuccess: (res) => {
                if (res && (res.status === 200 || res.status === 201 || res.status === 204)) {
                    toast.success('Xóa thành công');
                    queryClient.invalidateQueries(['soLieuNghienCuuData', hoSoId]);
                } else {
                    toast.error(res?.message || 'Có lỗi xảy ra khi xóa');
                }
            },
            onError: (err) => {
                toast.error(err?.message || 'Có lỗi xảy ra');
            }
        });

        setOpenConfirmDeleteModal(false);
        setIdWantDelete('');
    };

    const cancelDelete = () => {
        setIdWantDelete('');
        setOpenConfirmDeleteModal(false);
    };

    const renderTrangThai = (val) => {
        switch (val) {
            case trangThaiLaySoLieuValue.choTiepNhan: return <Badge variant="warning">Chờ tiếp nhận</Badge>;
            case trangThaiLaySoLieuValue.tiepNhanHoSo: return <Badge variant="info">Tiếp nhận hồ sơ</Badge>;
            case trangThaiLaySoLieuValue.chinhSua: return <Badge variant="secondary">Yêu cầu chỉnh sửa</Badge>;
            case trangThaiLaySoLieuValue.dangLaySoLieu: return <Badge variant="primary">Đang lấy số liệu</Badge>;
            case trangThaiLaySoLieuValue.hoanThanh: return <Badge variant="success">Hoàn thành</Badge>;
            case trangThaiLaySoLieuValue.tuChoi: return <Badge variant="danger">Từ chối</Badge>;
            default: return <Badge variant="secondary">Chưa xác định</Badge>;
        }
    };

    return (
        <>
        <Card className="h-100">
            <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                    <Nav variant="pills" activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="flex-row">
                        <Nav.Item>
                            <Nav.Link eventKey="noiLaySoLieu" className="font-weight-bold">Nơi lấy số liệu</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="quaTrinhLaySoLieu" className="font-weight-bold">Quá trình lấy số liệu</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="kiemTraSoLieu" className="font-weight-bold">Kiểm tra số liệu</Nav.Link>
                        </Nav.Item>
                    </Nav>
                    <Button variant="success" size="sm" onClick={handleAdd}>
                        <i className="fas fa-plus mr-1"></i> Thêm mới
                    </Button>
                </div>
            </Card.Header>
            <Card.Body>
                {isLoading ? (
                    <div className="text-center p-5">
                        <Spinner animation="border" variant="primary" />
                    </div>
                ) : (
                    <>
                        <div className="table-responsive">
                            <Table striped bordered hover>
                                <thead>
                                    <tr>
                                        <th className="text-center" width="50">STT</th>
                                        <th>Nơi lấy số liệu</th>
                                        <th className="text-center">Từ ngày</th>
                                        <th className="text-center">Đến ngày</th>
                                        <th className="text-center">File đính kèm</th>
                                        <th className="text-center">Trạng thái</th>
                                        <th>Ghi chú</th>
                                        <th className="text-center" width="120">Thao tác</th>
                                    </tr>
                                </thead>
                            <tbody>
                                {dataList && dataList.length > 0 ? (
                                    dataList.map((item, index) => (
                                        <tr key={item.id}>
                                            <td className="text-center">{index + 1}</td>
                                            <td>{item.noiLay}</td>
                                            <td className="text-center">
                                                {item.tuNgay ? moment(item.tuNgay).format('DD/MM/YYYY') : ''}
                                            </td>
                                            <td className="text-center">
                                                {item.denNgay ? moment(item.denNgay).format('DD/MM/YYYY') : ''}
                                            </td>
                                            <td className="text-center">
                                                {item.fileCuDiLay ? (
                                                    (() => {
                                                        const path = item.fileCuDiLay;
                                                        const baseUrl = BaseGatewayImage.endsWith('/') ? BaseGatewayImage.slice(0, -1) : BaseGatewayImage;
                                                        const safePath = path.startsWith('/') ? path : `/${path}`;
                                                        const fileUrl = path.startsWith('http') ? path : `${baseUrl}${safePath}`;
                                                        let name = path.split('/').pop() || 'File';
                                                        name = name.replace(/_\d{14}(?=\.\w+$|$)/, '');
                                                        return (
                                                            <a href={fileUrl} target="_blank" rel="noreferrer" className="btn btn-outline-primary btn-sm text-left mb-1 mr-1" style={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block' }} title={name}>
                                                                <i className="fas fa-paperclip mr-1"></i> {name}
                                                            </a>
                                                        );
                                                    })()
                                                ) : (
                                                    <span className="text-muted">Không có</span>
                                                )}
                                            </td>
                                            <td className="text-center">{renderTrangThai(item.trangThai)}</td>
                                            <td>{item.ghiChuChinhSua}</td>
                                            <td className="text-center">
                                                <div className="d-flex justify-content-center text-nowrap">
                                                    <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Sửa')}>
                                                        <Button 
                                                            variant="warning" 
                                                            size="sm" 
                                                            className="mr-1 text-white" 
                                                            onClick={() => handleEdit(item)}
                                                            disabled={item.trangThai !== trangThaiLaySoLieuValue.choTiepNhan && item.trangThai !== trangThaiLaySoLieuValue.chinhSua}
                                                        >
                                                            <i className="fas fa-edit"></i>
                                                        </Button>
                                                    </OverlayTrigger>
                                                    <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Xóa')}>
                                                        <Button 
                                                            variant="danger" 
                                                            size="sm" 
                                                            onClick={() => deleteAction(item.id, item.noiLay)}
                                                            disabled={item.trangThai !== trangThaiLaySoLieuValue.choTiepNhan && item.trangThai !== trangThaiLaySoLieuValue.chinhSua}
                                                        >
                                                            <i className="fas fa-trash"></i>
                                                        </Button>
                                                    </OverlayTrigger>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="8" className="text-center text-muted">
                                            Chưa có dữ liệu Nơi lấy số liệu
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                        </div>
                        <ConfirmModal
                            isOpen={openConfirmDeleteModal}
                            confirmType="delete"
                            goBackAfterDelete={false}
                            handleAccept={acceptDelete}
                            handleCancel={cancelDelete}
                            content={confirmDelMessage}
                        />
                    </>
                )}
            </Card.Body>
        </Card>

        {viewMode === 'add' && <NoiLaySoLieuAdd hoSoId={hoSoId} setViewMode={setViewMode} refreshList={refetch} />}
        {viewMode === 'edit' && dataEdit && <NoiLaySoLieuEdit hoSoId={hoSoId} dataEdit={dataEdit} setViewMode={setViewMode} refreshList={refetch} />}
        </>
    );
};

export default NoiLaySoLieu;
